import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ApplicantInformations } from '@/features/jobs/applications/$applicationId/applicant-informations'
import { ApplicationStage } from '@/features/jobs/applications/$applicationId/application-stage'
import { Activities } from '@/features/jobs/applications/$applicationId/shared/activities'
import { applicationsApplicantInfoQuery } from '@/hooks/use-applications-queries'
import { ApplicationTabsList } from '@/modules/application/presentation/components/ApplicationTabsList'
import { getFullName } from '@/utils/string'

const viewSchema = z.enum(['application', 'emails', 'interviews', 'files']).default('application')
export type ViewSchemaSearchParams = z.infer<typeof viewSchema>

export const Route = createFileRoute('/_authenticated/jobs_/$jobId/applications/$applicationId')({
  component: ApplicantsApplicationId,
  validateSearch: z.object({
    interviewId: z.string().optional(),
    messageId: z.string().optional(),
    view: viewSchema,
  }),
})

function ApplicantsApplicationId() {
  const { applicationId, jobId } = Route.useParams()
  const navigate = Route.useNavigate()

  const { data: applicantInfo } = useQuery(applicationsApplicantInfoQuery(applicationId))

  if (!applicantInfo) return null

  return (
    <Dialog
      onOpenChange={async (open) => {
        if (!open) {
          await navigate({
            params: { jobId },
            to: '/jobs/$jobId',
          })
        }
      }}
      open
    >
      <DialogContent className="h-[calc(100%-theme(spacing.8))] min-w-[calc(100%-theme(spacing.8))] overflow-hidden p-0">
        <DialogHeader className="hidden">
          <DialogTitle className="hidden">{getFullName(applicantInfo.applicant)}</DialogTitle>
          <DialogDescription className="hidden">{getFullName(applicantInfo.applicant)}</DialogDescription>
        </DialogHeader>
        <div className="grid h-full grid-cols-[_1fr,theme(spacing.96)] grid-rows-[_auto,_1fr] overflow-hidden bg-background">
          <ApplicantInformations applicationId={applicationId} />
          <ApplicationStage
            applicantId={applicantInfo?.applicant.id}
            applicationId={applicationId}
            jobTitle={applicantInfo?.job.title}
          />
          <ApplicationTabsList
            emailsCount={applicantInfo.emailsCount}
            interviewsCount={applicantInfo.interviewsCount}
          />
          <Activities />
        </div>
      </DialogContent>
    </Dialog>
  )
}
